import {useCallback, useEffect, useMemo, useRef} from "react";
import {useTemplateBuilder} from "./context";
import {useSafePuckFieldOnChange} from "./fields";
import {normalizeParagraphSegments, parseTemplateText, serializeTemplateText} from "./serialization";
import type {ParagraphSegment, ParagraphVariable} from "./types";
import {paragraphVariables} from "./variables";

const inspectorRoot = (editor: HTMLElement | null) =>
  editor?.closest(".template-builder-paragraph") ?? editor;

export const ParagraphInspector = ({
  value,
  onChange,
  instructions = "Write naturally, then insert approved document or form values at the cursor.",
  ariaLabel = "Paragraph content",
  multiline = true,
}: {
  value?: ParagraphSegment[];
  onChange: (segments: ParagraphSegment[]) => void;
  instructions?: string;
  ariaLabel?: string;
  multiline?: boolean;
}) => {
  const { inputDefinitions } = useTemplateBuilder();
  const incoming = useMemo(() => normalizeParagraphSegments(value), [value]);
  const incomingKey = JSON.stringify(incoming);
  const editorRef = useRef<HTMLDivElement>(null);
  const segmentsRef = useRef(incoming);
  const renderedKey = useRef<string | undefined>(undefined);
  const savedRange = useRef<Range | undefined>(undefined);
  const caretOffset = useRef<number | undefined>(undefined);
  const variables = useMemo(() => paragraphVariables(inputDefinitions), [inputDefinitions]);
  const variableLabels = useMemo(() => new Map(variables.map((item) => [item.variable, item.editorLabel])), [variables]);

  const persistOnChange = useSafePuckFieldOnChange(onChange);
  const onChangeRef = useRef(persistOnChange);
  onChangeRef.current = persistOnChange;
  const lastSentKey = useRef(incomingKey);
  const insertTimeout = useRef<number>();

  const createTextSpan = (text = "") => {
    const node = document.createElement("span");
    node.dataset.paragraphText = "true";
    node.textContent = text || "\u200B";
    return node;
  };
  const createChip = (variable: ParagraphVariable) => {
    const chip = document.createElement("span");
    chip.className = "template-builder-value-chip";
    chip.dataset.paragraphVariable = variable;
    chip.contentEditable = "false";
    chip.textContent = variableLabels.get(variable) ?? variable;
    const remove = document.createElement("button");
    remove.type = "button";
    remove.dataset.removeVariable = "true";
    remove.contentEditable = "false";
    remove.setAttribute("aria-label", `Remove ${variable}`);
    remove.textContent = "×";
    chip.append(remove);
    return chip;
  };

  // React deliberately does not reconcile the contents of this editable region:
  // the browser owns its children while the user types, then we persist ordered
  // text/token segments on blur or variable insertion.
  const renderEditor = useCallback((next: ParagraphSegment[]) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.replaceChildren();
    normalizeParagraphSegments(next).forEach((part) => {
      if ("text" in part) {
        editor.append(createTextSpan(part.text));
        return;
      }
      editor.append(createChip(part.variable));
    });
    if (editor.lastElementChild?.matches("[data-paragraph-variable]")) editor.append(createTextSpan());
  }, [variableLabels]);
  const readSegments = useCallback((): ParagraphSegment[] => {
    const editor = editorRef.current;
    if (!editor) return segmentsRef.current;
    const result: ParagraphSegment[] = [];
    const appendText = (text: string) => {
      const value = text.replace(/\u200B/g, "");
      if (!value) return;
      const previous = result[result.length - 1];
      if (previous && "text" in previous) previous.text += value;
      else result.push({ text: value });
    };
    const read = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) { appendText(node.nodeValue ?? ""); return; }
      if (!(node instanceof HTMLElement)) return;
      const variable = node.dataset.paragraphVariable;
      if (variable) {
        const approvedVariable = variables.find((item) => item.variable === variable)?.variable;
        if (approvedVariable) result.push({ variable: approvedVariable });
        else appendText(node.textContent ?? "");
        return;
      }
      if (node.tagName === "BR") { appendText("\n"); return; }
      const isBlock = /^(DIV|P|LI)$/.test(node.tagName);
      node.childNodes.forEach(read);
      if (isBlock && result.length) appendText("\n");
    };
    editor.childNodes.forEach(read);
    return normalizeParagraphSegments(result);
  }, [variables]);
  const persist = useCallback((next: ParagraphSegment[], redraw = false) => {
    const normalized = normalizeParagraphSegments(next);
    const nextKey = JSON.stringify(normalized);
    segmentsRef.current = normalized;
    if (redraw) renderEditor(normalized);
    if (nextKey === lastSentKey.current) return;
    if (!onChangeRef.current(normalized)) return;
    lastSentKey.current = nextKey;
  }, [renderEditor]);

  const rememberSelection = () => {
    const selection = window.getSelection();
    const editor = editorRef.current;
    if (!selection?.rangeCount || !editor) return;
    if (!editor.contains(selection.anchorNode) || !editor.contains(selection.focusNode)) return;
    const range = selection.getRangeAt(0).cloneRange();
    savedRange.current = range;
    const prefix = range.cloneRange();
    prefix.selectNodeContents(editor);
    prefix.setEnd(range.startContainer, range.startOffset);
    caretOffset.current = prefix.toString().replace(/\u200B/g, "").length;
  };

  const rangeFromOffset = (editor: HTMLElement, offset: number) => {
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let remaining = offset;
    let node = walker.nextNode();
    while (node) {
      const raw = node.nodeValue ?? "";
      const visible = raw.replace(/\u200B/g, "");
      if (remaining <= visible.length) {
        let seen = 0;
        let index = 0;
        while (index < raw.length && seen < remaining) {
          if (raw[index] !== "\u200B") seen += 1;
          index += 1;
        }
        const range = document.createRange();
        range.setStart(node, index);
        range.collapse(true);
        return range;
      }
      remaining -= visible.length;
      node = walker.nextNode();
    }
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    return range;
  };

  const insertRange = () => {
    const editor = editorRef.current;
    if (!editor) return undefined;
    if (savedRange.current && editor.contains(savedRange.current.startContainer)) {
      return savedRange.current.cloneRange();
    }
    if (caretOffset.current !== undefined) return rangeFromOffset(editor, caretOffset.current);
    return undefined;
  };

  useEffect(() => {
    if (renderedKey.current === incomingKey) return;
    renderedKey.current = incomingKey;
    segmentsRef.current = incoming;
    if (incomingKey !== lastSentKey.current) lastSentKey.current = incomingKey;
    const active = document.activeElement;
    const root = inspectorRoot(editorRef.current);
    if (editorRef.current?.contains(active) || (active instanceof Node && root?.contains(active))) return;
    renderEditor(incoming);
  }, [incoming, incomingKey, renderEditor]);
  useEffect(() => {
    const onSelectionChange = () => rememberSelection();
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);
  useEffect(() => () => window.clearTimeout(insertTimeout.current), []);

  const insertVariable = (variable: ParagraphVariable) => {
    const editor = editorRef.current;
    if (!editor) return;
    const range = insertRange();
    if (range) {
      if (range.startContainer.nodeType === Node.TEXT_NODE && range.collapsed) {
        range.startContainer.parentElement?.normalize();
      }
      range.deleteContents();
      const occurrence = Array.from(editor.querySelectorAll<HTMLElement>("[data-paragraph-variable]")).filter((item) => item.dataset.paragraphVariable === variable).length;
      const spacer = createTextSpan();
      const chip = createChip(variable);
      range.insertNode(spacer);
      range.insertNode(chip);
      persist(readSegments(), false);
      requestAnimationFrame(() => {
        const inserted = Array.from(editor.querySelectorAll<HTMLElement>("[data-paragraph-variable]"))
          .filter((item) => item.dataset.paragraphVariable === variable)[occurrence];
        const text = inserted?.nextElementSibling?.firstChild;
        if (!text) return;
        const nextRange = document.createRange();
        nextRange.setStart(text, 0); nextRange.collapse(true);
        const nextSelection = window.getSelection();
        nextSelection?.removeAllRanges(); nextSelection?.addRange(nextRange); editor.focus();
        rememberSelection();
      });
    } else {
      persist(readSegments().concat({ variable }), true);
      requestAnimationFrame(() => {
        const text = editor.lastElementChild?.matches("[data-paragraph-text]")
          ? editor.lastElementChild.firstChild
          : undefined;
        if (!text) return;
        const nextRange = document.createRange();
        nextRange.setStart(text, text.nodeValue?.length ?? 0);
        nextRange.collapse(true);
        const nextSelection = window.getSelection();
        nextSelection?.removeAllRanges(); nextSelection?.addRange(nextRange); editor.focus();
        rememberSelection();
      });
    }
    savedRange.current = undefined;
  };
  return <div className="template-builder-paragraph">
    <p>{instructions}</p>
    <div ref={editorRef} className="template-builder-paragraph-composer" role="textbox" aria-label={ariaLabel} aria-multiline={multiline} contentEditable spellCheck={false} suppressContentEditableWarning onInput={() => { segmentsRef.current = readSegments(); rememberSelection(); }} onBlur={(event) => {
      if (event.relatedTarget instanceof Node && inspectorRoot(editorRef.current)?.contains(event.relatedTarget)) return;
      persist(readSegments(), false);
    }} onKeyDown={(event) => { if (!multiline && event.key === "Enter") event.preventDefault(); }} onKeyUp={rememberSelection} onMouseUp={rememberSelection} onClick={(event) => {
      const remove = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("[data-remove-variable]") : undefined;
      const chip = remove?.closest<HTMLElement>("[data-paragraph-variable]");
      if (!chip) return;
      chip.remove(); persist(readSegments(), true);
    }} onMouseDown={(event) => { if (event.target instanceof Element && event.target.closest("[data-remove-variable]")) event.preventDefault(); }}/>
    <label>Insert automatic value
      <select defaultValue="" onMouseDownCapture={rememberSelection} onKeyDown={rememberSelection} onChange={(event) => {
        const variable = variables.find((item) => item.variable === event.target.value)?.variable;
        event.currentTarget.value = "";
        if (!variable) return;
        window.clearTimeout(insertTimeout.current);
        insertTimeout.current = window.setTimeout(() => insertVariable(variable), 0);
      }}>
        <option value="" disabled>Select a document or form value</option>
        {Array.from(new Set(variables.map((item) => item.group))).map((group) => <optgroup key={group} label={group}>
          {variables.filter((item) => item.group === group).map((item) => <option key={item.variable} value={item.variable}>{item.label} — {item.sample}</option>)}
        </optgroup>)}
      </select>
    </label>
  </div>;
};

export const TemplateStringInspector = ({
  value,
  onChange,
  instructions = "Write naturally, then insert approved document or form values at the cursor.",
  ariaLabel,
  multiline = false,
}: {
  value?: string;
  onChange: (value: string) => void;
  instructions?: string;
  ariaLabel?: string;
  multiline?: boolean;
}) => {
  const persist = useRef(onChange);
  persist.current = onChange;
  return <ParagraphInspector
    value={parseTemplateText(value)}
    onChange={(segments) => persist.current(serializeTemplateText(segments))}
    instructions={instructions}
    ariaLabel={ariaLabel}
    multiline={multiline}
  />;
};
